import React, { FC } from 'react';
import styled from '@emotion/styled';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { Box } from '~Styles';
import { handyBreakpoint } from '~Styles/breakpoints';
import { useDeviceHandler } from '~Components/DeviceProvider';
import { useDispatch, useI18n } from '~Hooks';
import * as actions from '~actions';

const RadioButtonContainer = styled.div<{ disabled: boolean; disablePointerEvents?: boolean }>`
  font-size: 20px;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'auto')};
  pointer-events: ${({ disablePointerEvents }) => (disablePointerEvents ? 'none' : 'auto')};

  h5 {
    line-height: normal;
  }
`;

const InnerContainer = styled.div`
  padding: 12px 16px;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  gap: 8px;
`;

const RadioButtonGroup = styled.div`
  font-size: 16px;
  font-weight: 500;
  text-overflow: ellipsis;
  display: flex;
  flex-direction: row;
  flex-flow: wrap;
  gap: 10px;
  color: var(--colorWhite);

  ${handyBreakpoint} {
    flex-direction: column;
    gap: 16px;

    & > div {
      gap: 8px;
    }
  }
`;

const RadioButton = styled.input`
  font-size: 20px;
  gap: 10px;
  height: 20px;
  width: 20px;
  border: none;
  vertical-align: middle;
  accent-color: var(--colorUIGreen);
`;

const Text = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-right: 16px;
`;

interface RadioButtonOption {
  id: string;
  label: string;
  value: any;
}

interface Props {
  data: {
    containerLabel: string;
    containerDesc?: string;
    name: string;
    buttons: RadioButtonOption[];
  };
  selected: string;
  handleChange: (value: any, id: string) => void;
  /** If provided, this component is rendered in Settings and we need to check if it's remotely controlled */
  settingKey?: string;
}

const RadioButtonSelect: FC<Props> = ({ data, selected, handleChange, settingKey }) => {
  const { i18n } = useI18n();
  const dispatch = useDispatch();
  const deviceHandler = useDeviceHandler();

  const isRemotelyControlled = settingKey && deviceHandler.remotelyControlledData[settingKey];
  const isDisabled = Boolean(isRemotelyControlled);

  const isHandy = window.innerWidth <= 600;

  const onChange = (value: any, id: string) => {
    /** We don't have space to display a paragraph so we show a toast if clicked instead informing that it's remotely controlled */
    if (isDisabled && isHandy) {
      dispatch(actions.addToast(i18n('Settings.LockedByBaemingo.Toast'), true));

      return;
    }

    handleChange(value, id);
  };

  return (
    <RadioButtonContainer disabled={isDisabled} disablePointerEvents={isDisabled && !isHandy}>
      <InnerContainer>
        <Text>
          <Heading level={5} color="var(--colorWhite)">
            {data.containerLabel}
          </Heading>
          {data.containerDesc && (
            <Paragraph level={5} color="var(--colorBlueGray300)">
              {data.containerDesc}
            </Paragraph>
          )}
        </Text>

        <Box display="flex" flexDirection="column" alignItems="flex-end" gap="xs">
          <RadioButtonGroup>
            {data.buttons.map((button) => (
              <Box display="flex" alignItems="center" gap="xs" key={button.id}>
                <RadioButton
                  type="radio"
                  id={button.id}
                  name={data.name}
                  checked={selected === button.id}
                  onChange={() => onChange(button.value, button.id)}
                />
                <label htmlFor={button.id}>{button.label}</label>
              </Box>
            ))}
          </RadioButtonGroup>
          {!isHandy && Boolean(isRemotelyControlled) && (
            <Paragraph level={5} color="var(--colorBlueGray300)">
              ({i18n('Settings.LockedByBaemingo')})
            </Paragraph>
          )}
        </Box>
      </InnerContainer>
    </RadioButtonContainer>
  );
};

export default RadioButtonSelect;
